import type { ResourceConfig } from '../interfaces/ResourceConfig';
import { resourceStore } from './resourceStore';
import { getMergedConfig } from './internal/getMergedConfig';
import { tryCatch } from '@shared/utils';

/**
 * @group Functions
 *
 * @summary
 * <blockquote>Gets the current ResourceConfig based on runtime environment</blockquote>
 *
 * @remarks
 * Returns the appropriate ResourceConfig based on the runtime environment:
 * 
 * **Server (Node.js):**
 * - Returns merged config from request-scoped AsyncLocalStorage stack
 * - Each HTTP request has its own isolated config
 * - Configs are merged in order (later configs override earlier ones)
 * - No interference between concurrent requests
 * 
 * **Client (Browser):**
 * - Returns merged config from component-scoped stack
 * - Managed by ClientResourceProvider component lifecycle
 * - Configs pushed during render, popped on unmount
 * 
 * If no configuration has been registered, returns null.
 *
 * @returns The current merged ResourceConfig or null if none registered
 *
 * @example
 * ```typescript
 * // Get the current resource configuration
 * const config = getResource();
 *
 * if (config?.logs?.development) {
 *   console.log('Logging enabled for this resource');
 * }
 *
 * // Use reporting config for event creation
 * const reportingConfig = config?.reporting;
 * if (reportingConfig?.development) {
 *   const event = {
 *     transport: reportingConfig.development.transport,
 *     samplingRate: reportingConfig.development.samplingRate,
 *   };
 * }
 * ```
 */
export function getResource(): ResourceConfig | null {
  // Server-side: use resourceStore (AsyncLocalStorage-based stack)
  if (typeof window === 'undefined') {
    const currentConfig = resourceStore.get();
    return currentConfig || null;
  }
  
  // Check if mobile app has set a global getMobileResource function
  // Mobile apps should set: globalThis.__getMobileResource = getMobileResource
  if (typeof globalThis !== 'undefined' && '__getMobileResource' in globalThis) {
    const mobileResourceFn = (globalThis as any).__getMobileResource;
    if (typeof mobileResourceFn === 'function') {
      const mobileConfig = mobileResourceFn();
      if (mobileConfig) {
        return mobileConfig;
      }
    }
  }
  
  // Client-side (Web): return merged config from component stack
  return getMergedConfig();
} 